import styled, { css } from 'styled-components';

import { Flex } from '@/components/Flex';
import { Typography } from '@/components/Typography';
import type { Maybe } from '@/lib/types';
import { theme } from '@/theme/getTheme';

type FontSizeMap = {
  [key: string]: string;
};

export const headingFontSizeDeskTopMap: FontSizeMap = {
  h1: theme.fontSizes.h1,
  h2: theme.fontSizes.h2,
  h3: theme.fontSizes.h3,
  h4: theme.fontSizes.h4,
  h5: theme.fontSizes.h5,
  h6: theme.fontSizes.h6,
};
export const headingFontSizeMobileMap: FontSizeMap = {
  h1: theme.fontSizes.h1Mobile,
  h2: theme.fontSizes.h2Mobile,
  h3: theme.fontSizes.h3Mobile,
  h4: theme.fontSizes.h4Mobile,
  h5: theme.fontSizes.h5Mobile,
  h6: theme.fontSizes.h6Mobile,
};

export const Title = styled.span<{
  $isHighlight?: boolean;
  headingHighlightColor?: Maybe<string>;
  headingType: string;
  headingVariation?: string;
  sizeDesktop: number | undefined;
  sizeMobile: number | undefined;
}>`
  &&& {
    ${({
      $isHighlight,
      headingHighlightColor,
      headingType,
      headingVariation,
      sizeDesktop,
      sizeMobile,
    }) => css`
      ${$isHighlight &&
      css`
        color: ${headingHighlightColor};
      `}
      line-height: ${['h4', 'h5', 'h6']?.includes(headingType) ? '1.2' : '1'};
      font-size: ${sizeMobile
        ? `${sizeMobile}px`
        : headingFontSizeMobileMap[headingType]};
      font-family: ${theme.fonts.titleFont};
      ${headingVariation === '140Years' &&
      css`
        font-family: ${theme.fontFamilies.breitlingFont};
        font-weight: 500;
      `}
      ${$isHighlight &&
      theme.components.heading?.fontWeight &&
      css`
        font-weight: ${theme.components.heading.fontWeight};
      `}
    ${theme.media.md.up} {
        font-size: ${sizeDesktop
          ? `${sizeDesktop}px`
          : headingFontSizeDeskTopMap[headingType]};
      }
    `}
  }
`;

export const ContentfulHeading = styled(Typography)<{
  $color: string;
  $scale: string;
  sizeDesktop: number;
  sizeMobile: number;
}>`
  ${({ $color, textAlign }) => css`
    color: ${$color};
    text-align: ${textAlign || 'left'};
  `}
`;
export const FormatHeadingBox = styled.span<{
  $color: string;
  $headingSeoTag: string;
  $headingType?: string;
  $mobileTextAlign?: string;
  textAlign?: string;
}>`
  ${({
    $color,
    $headingSeoTag,
    $headingType,
    $mobileTextAlign,
    textAlign,
    theme,
  }) => css`
    text-align: ${$mobileTextAlign || textAlign || 'center'};
    width: 100%;
    color: ${$color};
    font-size: ${$headingType && headingFontSizeMobileMap[$headingType]};
    font-family: ${theme.fontFamilies.italianPlate};
    font-weight: ${theme.fontWeights.bold};
    line-height: ${$headingType && ['h4', 'h5', 'h6']?.includes($headingType)
      ? '1.2'
      : '1'};
    ${theme.components.heading?.paddingBottom &&
    css`
      padding-bottom: ${theme.components.heading.paddingBottom};
    `}
    ${theme.media.md.up} {
      text-align: ${textAlign || 'center'};
      font-size: ${$headingType && headingFontSizeDeskTopMap[$headingType]};
      ${$headingSeoTag === 'h1' &&
      parseInt(theme.components.heading.blockWidth) > 0 &&
      css`
        &::before {
          content: ' ';
          display: inline-block;
          width: ${theme.components.heading.blockWidth};
          background: ${theme.colors.primary};
          border-right: ${theme.components.heading.blockBorder} solid
            ${theme.colors.secondary};
          margin-right: ${theme.components.heading.blockMargin};
          margin-bottom: 2px;
          position: relative;
          top: -4px;
        }
      `}
    }
  `}
`;

export const Heading = styled(Flex)<{
  $color: string;
  $isUppercase?: boolean;
  headingType?: string;
  isHeroSlider?: boolean;
  shouldApplyBottomPadding?: boolean;
  textAlign?: string;
}>`
  width: 100%;
  ${({ $isUppercase }) =>
    $isUppercase &&
    css`
      text-transform: uppercase;
    `}
  word-break: break-word;
  white-space: pre-wrap;
  gap: 1.5rem;
  align-items: stretch;
  ${({ $color, shouldApplyBottomPadding, textAlign, theme }) => css`
    line-height: normal;
    color: ${$color};
    text-align: ${textAlign || 'left'};
    ${shouldApplyBottomPadding &&
    css`
      margin-bottom: ${theme.spacing(3)};
      ${theme.media.md.up} {
        margin-bottom: ${theme.spacing(4)};
      }
    `}
  `}

  ${({ isHeroSlider, theme }) => css`
    ${theme.media.md.down} {
      ${isHeroSlider &&
      css`
        text-align: center;
      `}
    }
  `}
`;
